/**
 * Screen 2 — Meeting Room.
 *
 * Hosts the Zoom Web SDK Client View inside an `<iframe>` that points at
 * `/<URL_PATH>/zoom-host`. The host route is a bare shell that mounts the
 * SDK directly to its own `<body>`. Doing this inside an iframe is the only
 * way to keep Client View (full-page takeover) inside our split layout
 * (Zoom left, enrollment right).
 *
 * Parent ↔ child contract (same-origin postMessage):
 *
 *   child → parent  { type: 'zoom-host:connection-change', state, ... }
 *   parent → child  { type: 'zoom-host:leave' }
 *
 * State flips on `state:'connected'` / `'ended'|'failed'` drive
 * `Store.onZoomConnected()` / `Store.onZoomEnded()`. Leaders see no CTA;
 * prospects see "Enroll Now" once `Store.zoomLive` is true.
 */

import React from 'react'
import Store from './../../RecruitingMeetingPage.store'
import UserProfileService from './../../../../shared/services/UserProfile.service'
import formatMeetingStartLocal from './../../formatStartTime'

import './MeetingRoom.component.scss'

const URL_PATH = (process.env.REACT_APP_RECRUITING_MEETING_URL_PATH || 'recruiting-meeting').replace(/^\/+/, '')

const buildDisplayIdentity = ctx => {
  if (ctx.isLeader) {
    const fname = UserProfileService.get && UserProfileService.get('u_fname')
    const lname = UserProfileService.get && UserProfileService.get('u_lname')
    const display = [fname, lname].filter(Boolean).join(' ').trim()
    return {
      userName: display ? `${display} (Host)` : 'Host',
      userEmail: (UserProfileService.get && UserProfileService.get('u_email')) || '',
      role: 1,
    }
  }
  return {
    userName: ctx.prospectFirstName ? `${ctx.prospectFirstName} (Prospect)` : 'Prospect',
    userEmail: (ctx.prefillData && ctx.prefillData.pro_email) || '',
    role: 0,
  }
}

class MeetingRoom extends React.Component {
  _iframeRef = React.createRef()
  // Cached on the instance, NOT in state, NOT computed in render. Any rebuild
  // of the iframe src tears down the in-flight meeting. Set ONCE in
  // componentDidMount from the values present in Store at that moment.
  _iframeSrc = null
  _initError = null

  componentDidMount() {
    this._mounted = true

    const ctx = Store.meetingContext
    if (!ctx || !ctx.sdkJwt || !ctx.meetingNumber) {
      this._initError = 'Meeting is not yet available. Please wait a moment or contact your recruiter.'
      this.forceUpdate()
      return
    }

    const { userName, userEmail, role } = buildDisplayIdentity(ctx)

    const params = new URLSearchParams()
    params.set('sdkJwt', ctx.sdkJwt)
    params.set('mn', String(ctx.meetingNumber))
    params.set('pw', ctx.meetingPasscode || '')
    params.set('un', userName)
    params.set('ue', userEmail)
    params.set('role', String(role))
    params.set('leave', `${window.location.origin}/${URL_PATH}/meeting-ended`)
    this._iframeSrc = `/${URL_PATH}/zoom-host?${params.toString()}`

    window.addEventListener('message', this.handleMessage)
    // _iframeSrc is an instance field (intentionally not state — see header
    // comment), so React doesn't know to re-render after we set it.
    // forceUpdate once so the iframe actually mounts.
    this.forceUpdate()
  }

  componentWillUnmount() {
    this._mounted = false
    try {
      const child = this._iframeRef.current && this._iframeRef.current.contentWindow
      if (child) {
        child.postMessage({ type: 'zoom-host:leave' }, window.location.origin)
      }
    } catch (e) {
      // ignore — iframe may already be gone
    }
    window.removeEventListener('message', this.handleMessage)
  }

  handleMessage = event => {
    if (event.origin !== window.location.origin) return
    const iframeWindow = this._iframeRef.current && this._iframeRef.current.contentWindow
    if (!iframeWindow || event.source !== iframeWindow) return
    const data = event.data || {}
    if (data.type !== 'zoom-host:connection-change') return
    const state = data.state
    if (state === 'connected') {
      Store.onZoomConnected()
    } else if (state === 'ended' || state === 'failed') {
      Store.onZoomEnded()
    }
  }

  handleStart = () => {
    Store.startOpportunity()
  }

  render() {
    const ctx = Store.meetingContext || {}
    const isLeader = !!ctx.isLeader
    // CTA only after the SDK reports Connected. Prospects can't enroll
    // before there's actually a meeting going.
    const showCta = !isLeader && Store.zoomLive
    const initError = this._initError

    // Before the meeting is live, surface when it starts (in the viewer's tz)
    // so an early arrival isn't left staring at a blank room.
    const startsUpcoming = !Store.zoomLive && ctx.startsAt && new Date(ctx.startsAt).getTime() > Date.now()
    const startsNotice = startsUpcoming ? formatMeetingStartLocal(ctx.startsAt) : null

    return (
      <div className='rm-meeting'>
        {startsNotice && <div className='rm-meeting__starts-notice'>The meeting will start at {startsNotice}</div>}
        <div className='rm-meeting__video'>
          {initError ? (
            <div className='rm-meeting__overlay'>
              <div className='rm-meeting__overlay-card'>
                <p>{initError}</p>
              </div>
            </div>
          ) : (
            this._iframeSrc && (
              <iframe
                ref={this._iframeRef}
                className='rm-meeting__iframe'
                src={this._iframeSrc}
                title='Zoom Meeting'
                allow='camera; microphone; display-capture; autoplay; clipboard-write; clipboard-read'
                allowFullScreen
              />
            )
          )}
        </div>

        {showCta && (
          <div className='rm-meeting__cta-bar'>
            <button
              type='button'
              className='rm-meeting__cta'
              onClick={this.handleStart}
              disabled={Store.viewState === 'enrolling'}
            >
              Enroll Now
            </button>
          </div>
        )}
      </div>
    )
  }
}

export default MeetingRoom
